import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { isAdminRole, useAdminMe, type AdminMeUser } from './api'

interface AdminAuthCtx {
  user: AdminMeUser | null
  isLoading: boolean
  isAdmin: boolean
  refresh: () => Promise<void>
}

const Ctx = createContext<AdminAuthCtx | null>(null)

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, refetch } = useAdminMe()
  const value = useMemo<AdminAuthCtx>(
    () => ({
      user: data?.user ?? null,
      isLoading,
      isAdmin: isAdminRole(data?.user?.role),
      refresh: async () => {
        await refetch()
      },
    }),
    [data, isLoading, refetch],
  )
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAdminAuth(): AdminAuthCtx {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAdminAuth outside provider')
  return ctx
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const { user, isAdmin, isLoading } = useAdminAuth()
  const loc = useLocation()
  if (isLoading) {
    return <div className="flex h-screen items-center justify-center text-text2">Betöltés…</div>
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: loc.pathname }} replace />
  }
  if (!isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-6">
        <div className="w-full max-w-md rp-card-lg text-center">
          <h1 className="mb-2 font-display text-[22px] font-extrabold text-danger">Forbidden</h1>
          <p className="text-[13.5px] text-text2">
            Ez a felület csak admin jogosultsággal érhető el.
          </p>
          <p className="mt-4 text-[12px] text-text3">
            Aktuális szerepkör: <span className="font-mono">{user.role}</span>
          </p>
        </div>
      </div>
    )
  }
  return <>{children}</>
}
